// Shared Agenda primitives + the CreateEventModal component.
//
// Originally inline in src/pages/Agenda.tsx; extracted 06/05 so MirrorChat
// can reuse the same modal for Mirror-driven event creation (PR-2 Option B).
// Agenda.tsx imports back from here for its own EventCard / EditEventModal.
//
// Manual and extracted drafts share a reviewed, receipt-backed local writer.
// Event rows and reminders commit together; retries keep the original request.

import React, { useCallback, useEffect, useRef, useState } from "react";
import { motion, AnimatePresence, useMotionValue, type PanInfo } from "framer-motion";
import { Repeat, X } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { calendarInstant } from "@/lib/calendarAction";
import { agendaWhen } from "@/lib/agendaPresentation";
import { localeTagFor } from "@/i18n/locale";
import { useLanguage } from "@/i18n/LanguageProvider";
import { EVENT_DURATION_MIN_BY_TAG, REMINDER_MIN_BY_TAG, type EventTag as CanonicalEventTag } from "@/lib/agendaDurations";
import type { AgendaEvent } from "@/pages/Agenda";
import RemindersEditor from "@/components/agenda/RemindersEditor";
import { MAX_REMINDERS } from "@/lib/agendaReminders";
import { useLibraryIdentity } from "@/hooks/useLibraryIdentity";
import { saveReviewedAgendaCreate } from "@/lib/saveReviewedAgendaCreate";
import { useQuotaErrorSurface } from "@/hooks/useQuotaErrorSurface";
import { newAgendaOperationId, parseAgendaRecord, agendaOperationKey, readPendingAgendaAction, AgendaExecutorError, type LocalAgendaActionPrefill, type LocalAgendaReceiptReference, type AgendaResponse } from "@/lib/agendaExecutor";
import type { AgendaCreateDraft, AgendaRequest } from "../../../supabase/functions/_shared/agendaActionDomain";
import type { TaskSnapshot } from "@/lib/taskExecutor";

// ── Types ──────────────────────────────────────────────────────────────────

// Re-export EventTag from the canonical module so existing consumers
// (Agenda.tsx, MirrorChat.tsx, NoteScheduleSheet, etc.) keep their import
// path unchanged. The type is identical — the union literals match
// agendaDurations.EventTag byte-for-byte.
export type EventTag = CanonicalEventTag;

export const TAG_STYLES: Record<EventTag, { bg: string; text: string }> = {
  Focus:    { bg: "hsl(187 72% 50% / 0.15)", text: "hsl(187 72% 60%)" },
  Meeting:  { bg: "hsl(265 60% 55% / 0.18)", text: "hsl(265 70% 72%)" },
  Personal: { bg: "hsl(155 45% 35% / 0.25)", text: "hsl(155 55% 55%)" },
};

export const TAG_OPTIONS: EventTag[] = ["Focus", "Meeting", "Personal"];
export const TAG_KEYS: Record<EventTag, string> = {
  Focus: "agenda.tag.focus",
  Meeting: "agenda.tag.meeting",
  Personal: "agenda.tag.personal",
};

export const REMINDER_OPTIONS: { value: number | null; labelKey: string }[] = [
  { value: null, labelKey: "agenda.reminderNone" },
  { value: 10, labelKey: "agenda.reminder10" },
  { value: 15, labelKey: "agenda.reminder15" },
  { value: 30, labelKey: "agenda.reminder30" },
  { value: 60, labelKey: "agenda.reminder60" },
];

// Legacy hardcoded badge map. Kept exported for any straggling import but
// no longer used by EventCard (uses generic agenda.event.reminderMinutesBefore
// template instead — handles arbitrary values like the new Personal default 10).
export const REMINDER_BADGE_KEYS: Record<number, string> = {
  15: "agenda.reminderBadge15",
  30: "agenda.reminderBadge30",
  60: "agenda.reminderBadge60",
};

export const DAY_KEYS = [
  "agenda.dayMon", "agenda.dayTue", "agenda.dayWed",
  "agenda.dayThu", "agenda.dayFri", "agenda.daySat", "agenda.daySun",
];

// ── Helpers ────────────────────────────────────────────────────────────────

export function getWeekDays(base: Date): Date[] {
  const days: Date[] = [];
  const dow = base.getDay();
  const monday = new Date(base);
  monday.setDate(base.getDate() - ((dow + 6) % 7));
  for (let i = 0; i < 7; i++) {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    days.push(d);
  }
  return days;
}

export function isSameDay(a: Date, b: Date) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

export function formatTime(iso: string) {
  return new Date(iso).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

export function toLocalDatetimeString(d: Date): string {
  const pad = (n: number) => n.toString().padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// ── AgendaBottomSheet ──────────────────────────────────────────────────────
// Shared bottom-sheet wrapper: z-60 (above BottomNav z-50), drag-to-dismiss,
// proper mobile scroll, safe-area CTA footer.

interface BottomSheetProps {
  open: boolean;
  onClose: () => void;
  closeLabel?: string;
  dismissDisabled?: boolean;
  title: string;
  footer: React.ReactNode;
  children: React.ReactNode;
}

const DISMISS_THRESHOLD = 100;

export const AgendaBottomSheet: React.FC<BottomSheetProps> = ({ open, onClose, closeLabel, dismissDisabled = false, title, footer, children }) => {
  const sheetY = useMotionValue(0);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Only the handle/header triggers drag-to-dismiss
  const handleDragEnd = useCallback(
    (_: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
      if (!dismissDisabled && (info.offset.y > DISMISS_THRESHOLD || info.velocity.y > 500)) {
        onClose();
      }
    },
    [onClose, dismissDisabled],
  );

  // Reset drag position when opening
  useEffect(() => {
    if (open) sheetY.set(0);
  }, [open, sheetY]);

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Overlay — separate so drag doesn't affect it */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[60]"
            style={{ background: "rgba(0,0,0,0.6)" }}
            onClick={() => { if (!dismissDisabled) onClose(); }}
          />

          {/* Sheet */}
          <motion.div
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ type: "spring", damping: 28, stiffness: 300 }}
            className="fixed bottom-0 left-0 right-0 z-[61] flex justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            <div
              role="dialog"
              aria-modal="true"
              aria-label={title}
              className="w-full min-w-0 max-w-[440px] rounded-t-3xl overflow-hidden flex flex-col"
              style={{
                background: "#0d1117",
                border: "1px solid hsl(187 72% 50% / 0.15)",
                borderBottom: "none",
                maxHeight: "calc(90dvh - env(safe-area-inset-top, 0px))",
              }}
            >
              {/* Drag handle — ONLY this area is draggable */}
              <motion.div
                drag="y"
                dragConstraints={{ top: 0, bottom: 0 }}
                dragElastic={{ top: 0, bottom: 0.5 }}
                onDragEnd={handleDragEnd}
                className="shrink-0 cursor-grab active:cursor-grabbing touch-none"
              >
                <div className="flex justify-center pt-3 pb-1">
                  <div
                    className="w-10 h-1 rounded-full"
                    style={{ background: "hsl(218 14% 30%)" }}
                  />
                </div>

                {/* Header — also part of drag zone */}
                <div className="flex items-center justify-between px-6 pt-2 pb-4">
                  <h2 className="font-display text-[18px] font-semibold text-foreground">
                    {title}
                  </h2>
                  <button
                    type="button"
                    aria-label={closeLabel}
                    disabled={dismissDisabled}
                    onClick={(e) => { e.stopPropagation(); onClose(); }}
                    className="w-8 h-8 rounded-xl flex items-center justify-center"
                    style={{
                      background: "hsl(187 72% 50% / 0.08)",
                      border: "1px solid hsl(187 72% 50% / 0.15)",
                    }}
                  >
                    <X size={14} style={{ color: "hsl(187 72% 50%)" }} />
                  </button>
                </div>
              </motion.div>

              {/* Scrollable body — NOT draggable, free scroll + input interaction */}
              <div
                ref={scrollRef}
                className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden overscroll-contain px-6 pb-2"
                style={{ WebkitOverflowScrolling: "touch" } as React.CSSProperties}
              >
                {children}
              </div>

              {/* Footer CTA — pinned */}
              <div
                className="shrink-0 px-6 pt-3"
                style={{
                  paddingBottom: "max(1.5rem, env(safe-area-inset-bottom, 1.5rem))",
                  borderTop: "1px solid hsl(187 72% 50% / 0.08)",
                }}
              >
                {footer}
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

// ── CreateEventModal ───────────────────────────────────────────────────────

// Optional pre-fill payload — used by the voice-first capture flow and the
// Mirror chat agenda-extract flow to seed the modal with structured data.
// Manual creation passes nothing.
export interface CreateEventPrefill {
  title?: string;
  start_at?: string;        // ISO 8601
  end_at?: string | null;
  tag?: EventTag;
  notes?: string | null;
  location?: string | null;
  reminder_minutes?: number | null;
  // Multi-reminder support (Opção C-light, 15/05/26). Up to MAX_REMINDERS
  // entries, each is "minutes BEFORE start". When agenda-extract returns
  // both `reminder_minutes` (legacy) and `reminders[]`, the array wins;
  // `reminder_minutes` is kept for backward compat with older callers.
  reminders?: number[] | null;
  transcript_raw?: string | null;
  confidence?: "high" | "medium" | "low" | null;
  source?: "voice" | "manual" | "mirror";
  // Legacy callers may retain these locators. New reviewed creates preserve
  // client_action_id; external provider metadata is never supplied to a create.
  client_action_id?: string | null;
  external_id?: string | null;
  expected_user_id?: string;
  expected_session_id?: string;
  action_id?: string;
  operation_id?: string;
  task_id?: string;
  expected_task_snapshot?: TaskSnapshot;
}

interface CreateModalProps {
  open: boolean;
  onClose: () => void;
  // Canonical rows are published only for a fresh confirmed operation.
  // Historical recovery asks the module to refresh current facts.
  onCreated: (rows?: AgendaEvent[]) => void;
  onReceipt?: (receipt: LocalAgendaReceiptReference) => void | Promise<boolean>;
  defaultDate: Date;
  onXP?: (ownerId: string, eventId: string, isCurrent: () => boolean) => void | Promise<void>;
  prefill?: CreateEventPrefill | null;
}

export const CreateEventModal: React.FC<CreateModalProps> = (props) => {
  const identity = useLibraryIdentity();
  if (!props.open || !identity.ready || !identity.userId || props.prefill?.expected_user_id && props.prefill.expected_user_id !== identity.userId) return null;
  return <CreateEventForm key={[identity.userId, identity.generation, props.prefill?.operation_id, props.prefill?.client_action_id].join(":")} {...props} ownerId={identity.userId} />;
};
const CreateEventForm = ({ open, onClose, onCreated, onReceipt, defaultDate, onXP, prefill: incomingPrefill, ownerId }: CreateModalProps & { ownerId: string }) => {
  const { t, tf, lang } = useLanguage();
  const quota = useQuotaErrorSurface();
  const [prefill] = useState(() => incomingPrefill ? structuredClone(incomingPrefill) : null);
  // One owner-bound mount owns one draft. It is ready on the first paint,
  // including a rapid Save before passive effects have run.
  const [initial] = useState(() => {
    const start = new Date(defaultDate); start.setHours(new Date().getHours() + 1, 0, 0, 0);
    const tag = prefill?.tag ?? "Focus", startIso = prefill?.start_at ?? start.toISOString();
    const endIso = prefill?.end_at ?? new Date(new Date(startIso).getTime() + (EVENT_DURATION_MIN_BY_TAG[tag] ?? 60) * 60_000).toISOString();
    const tagReminder = REMINDER_MIN_BY_TAG[tag];
    const reminders = Array.isArray(prefill?.reminders) ? [...prefill.reminders]
      : prefill?.reminder_minutes !== undefined ? prefill.reminder_minutes === null ? [] : [prefill.reminder_minutes]
      : tagReminder == null ? [] : [tagReminder];
    return { tag, startIso, endIso, reminders };
  });
  const [title, setTitle] = useState(prefill?.title ?? "");
  const [tag, setTag] = useState<EventTag>(initial.tag);
  const [startAt, setStartAt] = useState(() => toLocalDatetimeString(new Date(initial.startIso)));
  const [endAt, setEndAt] = useState(() => toLocalDatetimeString(new Date(initial.endIso)));
  const [notes, setNotes] = useState(prefill?.notes ?? "");
  const [location, setLocation] = useState(prefill?.location ?? "");
  const [reminders, setReminders] = useState<number[]>(initial.reminders);
  const [repeatEnabled, setRepeatEnabled] = useState(false);
  const [repeatDays, setRepeatDays] = useState<number[]>([]); // 0=Mon..6=Sun
  const [shakeTitle, setShakeTitle] = useState(false);
  const [saving, setSaving] = useState(false);
  const [staged, setStaged] = useState<LocalAgendaActionPrefill | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [operationId, setOperationId] = useState(() => prefill?.operation_id ?? newAgendaOperationId());
  const ids = useRef(Array.from({ length: 7 }, newAgendaOperationId));
  const dateOrigins = useRef<{ start_at?: string; end_at?: string }>({ start_at: initial.startIso, end_at: initial.endIso });
  const completed = useRef(false);
  const epoch = useRef(0);
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => { if (session?.user.id !== ownerId) epoch.current++; });
    const life = epoch;
    return () => { life.current++; completed.current = false; subscription.unsubscribe(); };
  }, [ownerId]);

  // Resolve each civil copy separately: a valid Monday time can be absent on DST Sunday.
  const getRepeatStarts = (startIso: string): string[] => {
    // UTC is only civil-date arithmetic here; each resulting local time resolves below.
    const monday = new Date(startAt.slice(0, 10) + "T12:00:00Z");
    monday.setUTCDate(monday.getUTCDate() - ((monday.getUTCDay() + 6) % 7));
    return repeatDays.map((dayIdx) => {
      const day = new Date(monday); day.setUTCDate(monday.getUTCDate() + dayIdx);
      const input = day.toISOString().slice(0, 10) + startAt.slice(10);
      const instant = calendarInstant(input, startIso);
      if (!instant) throw new Error("invalid_local_time");
      return instant;
    });
  };

  const toggleRepeatDay = (idx: number) => {
    setRepeatDays((prev) =>
      prev.includes(idx) ? prev.filter((d) => d !== idx) : [...prev, idx].sort()
    );
  };

  const prepareCreate = (): LocalAgendaActionPrefill | null => {
    if (!title.trim()) { toast.error(t("agenda.titleRequired")); setShakeTitle(true); return null; }
    if (!startAt || !endAt) { setError(t("agendaAction.explicitEnd")); return null; }
    if (repeatEnabled && !repeatDays.length) { toast.error(t("agenda.selectDay")); return null; }
    try {
      if (reminders.length > MAX_REMINDERS || new Set(reminders).size !== reminders.length || reminders.some(value => !Number.isInteger(value) || value < 0 || value > 10080)) throw new Error("invalid_reminders");
      const startIso = calendarInstant(startAt, dateOrigins.current.start_at), endIso = calendarInstant(endAt, dateOrigins.current.end_at);
      if (!startIso || !endIso) throw new Error("invalid_local_time");
      const duration = new Date(endIso).getTime() - new Date(startIso).getTime();
      if (!Number.isFinite(duration) || duration <= 0) throw new Error("invalid_window");
      const dates = repeatEnabled ? getRepeatStarts(startIso) : [startIso];
      if (prefill?.task_id && dates.length !== 1) throw new Error("invalid_promotion");
      const drafts: AgendaCreateDraft[] = dates.map((date, index) => ({
        event_id: ids.current[index], title: title.trim(), tag, start_at: date, end_at: date === startIso ? endIso : new Date(new Date(date).getTime() + duration).toISOString(),
        notes: notes || null, location: location || null, all_day_start: null, all_day_end: null,
        reminders: reminders.map(reminder_minutes => ({ id: newAgendaOperationId(), reminder_minutes })), source: prefill?.source ?? (prefill?.expected_session_id ? "mirror" : "manual"),
        transcript_raw: prefill?.transcript_raw ?? null, confidence: prefill?.confidence ?? null,
        client_action_id: index === 0 && prefill?.client_action_id ? prefill.client_action_id : "agenda:" + ids.current[index],
      }));
      setError(null); completed.current = false;
      return { action: prefill?.task_id ? "promote_task" : repeatEnabled ? "create_batch" : "create", operation_id: operationId, action_id: prefill?.action_id ?? "standalone:" + operationId,
        event_id: drafts[0].event_id, draft: drafts[0], ...(repeatEnabled ? { events: drafts } : {}), source: drafts[0].source,
        transcript_raw: prefill?.transcript_raw, confidence: prefill?.confidence, client_action_id: drafts[0].client_action_id,
        expected_user_id: ownerId, expected_session_id: prefill?.expected_session_id,
        ...(prefill?.task_id ? { task_id: prefill.task_id, expected_task_snapshot: structuredClone(prefill.expected_task_snapshot!) } : {}),
      };
    } catch (cause) { setError(t(cause instanceof Error && cause.message === "invalid_local_time" ? "mirror.calendar.invalidDates" : "agendaAction.error")); return null; }
  };
  const saveLock = useRef(false);
  const handleSave = async () => {
    if (saveLock.current) return;
    const prepared = staged ?? prepareCreate();
    if (!prepared) return;
    saveLock.current = true; setSaving(true); setError(null);
    const generation = epoch.current;
    const isCurrent = () => generation === epoch.current;
    setStaged(prepared);
    try {
      const action = prepared.action;
      if (action !== 'create' && action !== 'create_batch' && action !== 'promote_task') throw new Error('review_required');
      const request: AgendaRequest = { action, operation_id: prepared.operation_id, event_id: prepared.event_id,
        confirmed: true, idempotency_key: agendaOperationKey(prepared.operation_id, action),
        ...(prepared.expected_session_id ? { mirror_context: { session_id: prepared.expected_session_id, action_id: prepared.action_id } } : {}) };
      if (action === 'create_batch') request.events = structuredClone(prepared.events!);
      else Object.assign(request, structuredClone(prepared.draft!));
      if (action === 'promote_task') {
        request.task_id = prepared.task_id;
        request.expected_task_snapshot = structuredClone(prepared.expected_task_snapshot!);
      }
      const response = await saveReviewedAgendaCreate({ ownerId, request, language: lang, isCurrent, onReceipt });
      if (!isCurrent()) return;
      await confirmed(response);
      if (!isCurrent()) return;
      toast.success(t('agenda.eventCreated'));
      onClose();
    } catch (cause) {
      if (!isCurrent()) return;
      const code = cause instanceof Error ? cause.message : '';
      if (!await quota(cause) && isCurrent()) setError(t(code === 'agenda_ack_pending' ? 'agendaAction.ackPending'
        : code === 'agenda_previous_pending' ? 'agendaAction.previousPending'
        : code === 'invalid_session' ? 'mirror.connector.accountChanged'
        : code === 'agenda_write_failed' ? 'agendaAction.error' : 'mirror.connector.outcomeUnknown'));
      if (code === 'agenda_changed' && isCurrent()) {
        toast.error(t('agendaAction.changed')); onCreated(); onClose(); return;
      }
      const released = code === 'agenda_write_failed' || cause instanceof AgendaExecutorError && cause.safe_retry && !await readPendingAgendaAction(ownerId);
      if (released && isCurrent()) {
        setStaged(null); setOperationId(newAgendaOperationId());
        ids.current = Array.from({ length: 7 }, newAgendaOperationId);
      }
    } finally {
      if (isCurrent()) { saveLock.current = false; setSaving(false); }
    }
  };
  const confirmed = async (response: AgendaResponse) => {
    const generation = epoch.current;
    completed.current = true;
    const result = response.result;
    const raw = result?.events ?? (result?.event ? [result.event] : []);
    const rows = Array.isArray(raw) ? raw.map(parseAgendaRecord).filter(row => row !== null) : [];
    // Historical recovery refreshes current reads; it never prepends old receipt rows or awards XP.
    const fresh = response.action !== "receipt" && response.replayed !== true;
    if (!prefill?.expected_session_id) onCreated(fresh ? rows as AgendaEvent[] : undefined);
    if (fresh && rows.length && onXP) {
      let deadline: ReturnType<typeof setTimeout> | undefined;
      try { await Promise.race([Promise.resolve(onXP(ownerId, rows[0].id, () => generation === epoch.current && completed.current)), new Promise<void>(resolve => { deadline = setTimeout(resolve, 10000); })]); }
      catch { /* The saved event remains confirmed if optional XP feedback fails. */ }
      finally { if (deadline) clearTimeout(deadline); }
    }
  };

  const inputStyle = {
    background: "hsl(var(--card))",
    border: "1px solid hsl(187 72% 50% / 0.15)",
  };

  const labelStyle = { color: "hsl(var(--muted-foreground))" };

  const createFooter = (
    <button
      onClick={handleSave}
      disabled={saving}
      className="w-full py-3.5 rounded-xl font-display text-[14px] font-semibold tracking-wide transition-all duration-200 active:scale-[0.98]"
      style={{
        background: saving ? "hsl(187 72% 50% / 0.25)" : "hsl(187 72% 50%)",
        color: "hsl(var(--background))",
      }}
    >
      {saving
        ? t("agenda.saving")
        : staged ? t("mirror.connector.checkResult")
        : repeatEnabled && repeatDays.length > 1
        ? tf("agenda.createEvents", { count: String(repeatDays.length) })
        : t("agenda.saveEvent")}
    </button>
  );

  const showYouSaid =
    (prefill?.source === "voice" || prefill?.source === "mirror") &&
    !!prefill?.transcript_raw;

  let datePreview: string[] = [];
  try {
    const start = calendarInstant(startAt, dateOrigins.current.start_at);
    const end = calendarInstant(endAt, dateOrigins.current.end_at);
    if (start && end && new Date(end).getTime() > new Date(start).getTime()) {
      const duration = new Date(end).getTime() - new Date(start).getTime();
      datePreview = (repeatEnabled ? getRepeatStarts(start) : [start]).map(value =>
        agendaWhen({ start_at: value, end_at: value === start ? end : new Date(new Date(value).getTime() + duration).toISOString() }, localeTagFor(lang)));
    }
  } catch { /* Invalid civil dates are explained when Save is pressed. */ }

  return (
    <AgendaBottomSheet
      open={open}
      onClose={onClose}
      title={t(prefill?.source === "mirror" || prefill?.expected_session_id ? "mirror.calendar.destination.vyv" : "agenda.newEvent")}
      closeLabel={t("common.close")}
      dismissDisabled={saving}
      footer={createFooter}
    >
      {error && <p role="alert" className="mb-3 text-sm">{error}</p>}
      {(prefill?.source === "mirror" || prefill?.expected_session_id) && <p className="mb-4 text-sm text-muted-foreground">
        {t("mirror.calendar.destination.vyvDescription")}
      </p>}
      <fieldset disabled={saving || !!staged} className="min-w-0">
      {/* Voice / Mirror raw-text reference. For voice this is the Whisper
          transcript; for Mirror it's the user's chat message verbatim. */}
      {showYouSaid && <details className="mb-4 rounded-xl border border-primary/15 bg-primary/5 px-3 py-2.5 text-sm">
        <summary className="cursor-pointer text-muted-foreground">{t("agenda.voice.youSaid")}</summary>
        <p className="mt-2 break-words text-muted-foreground">{prefill?.transcript_raw}</p>
      </details>}
      {/* Title */}
      <label className="block mb-4">
        <span className="text-sm font-medium mb-1.5 block" style={labelStyle}>
          {t("agenda.eventTitle")}
        </span>
        <textarea
          maxLength={1000}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder={t("agenda.eventTitlePlaceholder")}
          className="w-full rounded-xl px-4 py-3 text-[14px] font-display text-foreground placeholder:text-muted-foreground outline-none transition-all"
          style={{
            ...inputStyle,
            ...(shakeTitle ? { animation: "inputShake 0.4s ease", borderColor: "hsl(0 70% 50% / 0.60)" } : {}),
          }}
        />
      </label>

      {/* Location (voice/Mirror events surface this; manual users can add too) */}
      <label className="block mb-4">
        <span className="text-sm font-medium mb-1.5 block" style={labelStyle}>
          {t("agenda.location")} <span style={{ color: "hsl(218 14% 45%)" }}>{t("agenda.optional")}</span>
        </span>
        <input
          value={location}
          onChange={(e) => setLocation(e.target.value)}
          placeholder={t("agenda.locationPlaceholder")}
          className="w-full rounded-xl px-4 py-3 text-[13px] font-display text-foreground placeholder:text-muted-foreground outline-none transition-all"
          style={inputStyle}
        />
      </label>

      {/* Tag */}
      <div className="mb-4">
        <span className="text-sm font-medium mb-1.5 block" style={labelStyle}>
          {t("agenda.tag")}
        </span>
        <div className="flex gap-2">
          {TAG_OPTIONS.map((tOpt) => {
            const s = TAG_STYLES[tOpt] ?? TAG_STYLES.Personal;
            const active = tag === tOpt;
            return (
              <button
                key={tOpt}
                onClick={() => setTag(tOpt)}
                className="flex-1 py-2 rounded-xl text-[12px] font-display font-medium tracking-wide transition-all duration-200"
                style={{
                  background: active ? s.bg : "transparent",
                  border: `1px solid ${active ? s.text : "hsl(222 18% 20%)"}`,
                  color: active ? s.text : "hsl(218 14% 55%)",
                }}
              >
                {t(TAG_KEYS[tOpt] ?? TAG_KEYS.Personal)}
              </button>
            );
          })}
        </div>
      </div>

      <div className="mb-4 space-y-1 text-sm text-muted-foreground" data-testid="agenda-create-dates">
        {datePreview.map((value, index) => <p key={index}>{value}</p>)}
      </div>

      {/* Start / End */}
      <div className="flex flex-col gap-3 mb-4">
        <label className="block">
          <span className="text-sm font-medium mb-1.5 block" style={labelStyle}>
            {t("agenda.start")}
          </span>
          <input
            type="datetime-local"
            value={startAt}
            onChange={(e) => setStartAt(e.target.value)}
            className="w-full rounded-xl px-3 py-2.5 text-[13px] font-display text-foreground outline-none"
            style={{ ...inputStyle, colorScheme: "dark" }}
          />
        </label>
        <label className="block">
          <span className="text-sm font-medium mb-1.5 block" style={labelStyle}>
            {t("agenda.end")}
          </span>
          <input
            type="datetime-local"
            value={endAt}
            onChange={(e) => setEndAt(e.target.value)}
            className="w-full rounded-xl px-3 py-2.5 text-[13px] font-display text-foreground outline-none"
            style={{ ...inputStyle, colorScheme: "dark" }}
          />
        </label>
      </div>

      {/* Reminders — multi-entry, capped at MAX_REMINDERS (2). Replaces
          the legacy single-chip selector; persisted to event_reminders
          on save. Pre-launch each entry shows the "Push active 15/06"
          notice so users know infra is in place but disparo is W6. */}
      <RemindersEditor
        reminders={reminders}
        onChange={setReminders}
      />

      {repeatEnabled && <p className="mb-3 text-sm text-muted-foreground">{t("agendaAction.finiteCopies")}</p>}
      {/* Repeat on selected days */}
      <div className="mb-4">
        <button
          type="button"
          disabled={!!prefill?.task_id}
          onClick={() => {
            setRepeatEnabled((v) => !v);
            if (repeatEnabled) setRepeatDays([]);
          }}
          className="flex items-center gap-2 mb-2"
        >
          <div
            className="w-8 h-[18px] rounded-full relative transition-all duration-200"
            style={{
              background: repeatEnabled ? "hsl(187 72% 50% / 0.35)" : "hsl(222 18% 20%)",
            }}
          >
            <div
              className="absolute top-[2px] w-[14px] h-[14px] rounded-full transition-all duration-200"
              style={{
                left: repeatEnabled ? "15px" : "2px",
                background: repeatEnabled ? "hsl(187 72% 55%)" : "hsl(218 14% 45%)",
              }}
            />
          </div>
          <span
            className="text-sm font-medium"
            style={{ color: repeatEnabled ? "hsl(187 72% 55%)" : "hsl(187 72% 50% / 0.55)" }}
          >
            <Repeat size={9} className="inline mr-1 -mt-px" />
            {t("agenda.repeatDays")}
          </span>
        </button>

        {repeatEnabled && !prefill?.task_id && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="flex gap-1.5"
          >
            {DAY_KEYS.map((dayKey, idx) => {
              const active = repeatDays.includes(idx);
              return (
                <button
                  key={dayKey}
                  onClick={() => toggleRepeatDay(idx)}
                  className="flex-1 py-2 rounded-xl text-[10px] font-display font-medium tracking-wide transition-all duration-200"
                  style={{
                    background: active ? "hsl(187 72% 50% / 0.18)" : "transparent",
                    border: `1px solid ${active ? "hsl(187 72% 50% / 0.45)" : "hsl(222 18% 20%)"}`,
                    color: active ? "hsl(187 72% 55%)" : "hsl(218 14% 50%)",
                  }}
                >
                  {t(dayKey)}
                </button>
              );
            })}
          </motion.div>
        )}
      </div>

      {/* Notes */}
      <label className="block mb-2">
        <span className="text-sm font-medium mb-1.5 block" style={labelStyle}>
          {t("agenda.notes")} <span style={{ color: "hsl(218 14% 45%)" }}>{t("agenda.optional")}</span>
        </span>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={2}
          placeholder={t("agenda.notesPlaceholder")}
          className="w-full rounded-xl px-4 py-3 text-[13px] font-display text-foreground placeholder:text-muted-foreground outline-none resize-none"
          style={inputStyle}
        />
      </label>
      </fieldset>
    </AgendaBottomSheet>
  );
};

export default CreateEventModal;
